import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiChannel } from 'core/constants';

import { ModelBase } from 'store/model-base';
import { buildHeaders, XmStore } from 'core/services';

import { StoreAction } from 'store/actions';
import { NowDeviceCollection, NowLineAutoPayCollection } from './models';
import { NowBillingSummaryResponse } from './models/billing-summary';
import { NowLineDetails } from './models/line-details';
import { NowLineBadgesCollection } from './models/badges';
import { NowPauseCancelPort } from './models/pause-cancel-port';
import { NowPortOutSendPin } from './models/send-pin';
import { NowPortOutChangeNotify } from './models/change-notify';
import { NowPortOutAuthCheck } from './models/port-out-auth';

@Injectable()
export class NowLineApi {
    public static getNowLineDetails(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowLineDetails> {
        return http.get(`/now/mobile/line/${params.lineId}`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowLineDetails.create<NowLineDetails>({ ...response, lineId: params.lineId }))
        );
    }

    public static getNowBillingSummary(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowBillingSummaryResponse> {
        return http.get('/now/billing/summary', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowBillingSummaryResponse.create<NowBillingSummaryResponse>({ ...response, lineId: params.lineId }))
        );
    }

    public static getNowDeviceCollection(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowDeviceCollection> {
        return http.get(`/now/mobile/line/${params.lineId}/device`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowDeviceCollection.create<NowDeviceCollection>({ ...response, lineId: params.lineId }))
        );
    }

    public static getNowLineBadges(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowLineBadgesCollection> {
        return http.get(`/now/badges/line/${params.lineId}`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowLineBadgesCollection.create<NowLineBadgesCollection>({ ...response, status: params.status }))
        );
    }

    public static getNowLineAutoPay(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowLineAutoPayCollection> {
        return http.get(`/now/autopay/line/${params.lineId}`, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowLineAutoPayCollection.create<NowLineAutoPayCollection>({ ...response, lineId: params.lineId }))
        );
    }

    public static updateNickName(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<ApiResponse> {
        return http.post(`/now/mobile/displayname/${params.lineId}`, params.payload, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        });
    }
    
    public static getPauseCancelPort(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowPauseCancelPort> {
        return http.get('/now/mobile/pause-cancel-port', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowPauseCancelPort.create<NowPauseCancelPort>({ ...response, status: params.status }))
        );
    }

    public static sendPin(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowPortOutSendPin> {
        return http.post('/now/portout/send/pin', params.payLoad, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) => NowPortOutSendPin.create<NowPortOutSendPin>(response))
        );
    }

    public static getPortOutChangeNotify(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<NowPortOutChangeNotify> {
        return http.post('/now/portout/change/notify', params, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: ApiResponse) =>
                NowPortOutChangeNotify.create<NowPortOutChangeNotify>({ ...response, status: params.status }))
        );
    }

    public static checkNowPortOutAuthorization(_xmStore: XmStore, http: HttpClient): Observable<NowPortOutAuthCheck> {
        return http.get('/step-up/port-out', {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY })
        }).pipe(
            map((response: NowPortOutAuthCheck) =>
                NowPortOutAuthCheck.create<NowPortOutAuthCheck>(response))
        );
    }
}

ModelBase.fetchMapping[StoreAction.GET_NOW_LINE_DETAILS] = NowLineApi.getNowLineDetails;
ModelBase.fetchMapping[StoreAction.GET_NOW_DEVICES] = NowLineApi.getNowDeviceCollection;
ModelBase.fetchMapping[StoreAction.GET_NOW_BILLING_SUMMARY] = NowLineApi.getNowBillingSummary;
ModelBase.fetchMapping[StoreAction.GET_NOW_LINE_BADGES] = NowLineApi.getNowLineBadges;
ModelBase.persistMapping[StoreAction.POST_NOW_UPDATE_NICK_NAME] = NowLineApi.updateNickName;
ModelBase.fetchMapping[StoreAction.GET_NOW_LINE_AUTO_PAY] = NowLineApi.getNowLineAutoPay;
ModelBase.fetchMapping[StoreAction.GET_NOW_PAUSE_CANCEL_PORT] = NowLineApi.getPauseCancelPort;
ModelBase.persistMapping[StoreAction.POST_NOW_PORT_OUT_SEND_PIN] = NowLineApi.sendPin;
ModelBase.persistMapping[StoreAction.GET_NOW_PORT_OUT_CHANGE_NOTIFY] = NowLineApi.getPortOutChangeNotify;
ModelBase.fetchMapping[StoreAction.GET_NOW_PORT_OUT_AUTH_CHECK] = NowLineApi.checkNowPortOutAuthorization;

